import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useState, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ApiError } from './api/client';
import { AppShell } from './components/AppShell';
import { CardSkeleton, EmptyState, ErrorState } from './components/ui';
import type { PageKey } from './lib/permissions';
import { OnboardingPage } from './pages/OnboardingPage';
import { SignInPage } from './pages/SignInPage';
import { SignUpPage } from './pages/SignUpPage';
import { TrialEndedPage } from './pages/TrialEndedPage';
import { ServicesProvider, useServices, type Services } from './services';
import { SessionProvider, useMeQuery, useSession } from './state/session';

const TodayPage = lazy(() => import('./pages/TodayPage'));
const DailyPage = lazy(() => import('./pages/DailyPage'));
const HourlyPage = lazy(() => import('./pages/HourlyPage'));
const ChannelsPage = lazy(() => import('./pages/ChannelsPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const MenuPage = lazy(() => import('./pages/MenuPage'));
const AlertsPage = lazy(() => import('./pages/AlertsPage'));
const AccuracyPage = lazy(() => import('./pages/AccuracyPage'));
const InputsPage = lazy(() => import('./pages/InputsPage'));
const InfluencersPage = lazy(() => import('./pages/InfluencersPage'));
const UploadPage = lazy(() => import('./pages/UploadPage'));
const SiteSettingsPage = lazy(() => import('./pages/SiteSettingsPage'));
const GroupSettingsPage = lazy(() => import('./pages/GroupSettingsPage'));
const DataSharingPage = lazy(() => import('./pages/DataSharingPage'));
const GroupPage = lazy(() => import('./pages/GroupPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const PrepPage = lazy(() => import('./pages/PrepPage'));
const QuietDaysPage = lazy(() => import('./pages/QuietDaysPage'));

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        refetchOnWindowFocus: false,
        retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
      },
    },
  });
}

function Guard({ page, children }: { page: PageKey; children: ReactNode }) {
  const { can } = useSession();
  if (!can(page)) {
    return (
      <EmptyState title="This page isn’t available for your role">
        Ask your group owner if you need access.
      </EmptyState>
    );
  }
  return <Suspense fallback={<CardSkeleton />}>{children}</Suspense>;
}

function AuthedApp() {
  const { auth } = useServices();
  const location = useLocation();
  const signedIn = useQuery({ queryKey: ['auth'], queryFn: () => auth.isSignedIn(), staleTime: Infinity });
  const me = useMeQuery(signedIn.data === true);

  if (signedIn.isPending || (signedIn.data && me.isPending)) {
    return (
      <div className="auth-page" aria-busy="true">
        <p className="muted">Loading DemandPilot…</p>
      </div>
    );
  }
  if (!signedIn.data) return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  if (me.isError) {
    if (me.error instanceof ApiError && me.error.status === 401) return <Navigate to="/sign-in" replace />;
    // Signed up and verified, but no business yet: finish setting up the free trial
    if (me.error instanceof ApiError && me.error.code === 'no_account') return <OnboardingPage />;
    return (
      <div className="auth-page">
        <ErrorState error={me.error} onRetry={() => me.refetch()} />
      </div>
    );
  }
  if (!me.data) return null;
  const status = me.data.subscription?.status;
  if (status === 'expired' || status === 'cancelled') return <TrialEndedPage me={me.data} />;
  if (me.data.locations.length === 0) {
    return (
      <div className="auth-page">
        <EmptyState title="No sites yet">Your account isn’t linked to any site. Ask your group owner to give you access.</EmptyState>
      </div>
    );
  }
  return (
    <SessionProvider me={me.data}>
      <AppShell>
        <Routes>
          <Route index element={<Guard page="today"><TodayPage /></Guard>} />
          <Route path="daily" element={<Guard page="daily"><DailyPage /></Guard>} />
          <Route path="hourly" element={<Guard page="hourly"><HourlyPage /></Guard>} />
          <Route path="channels" element={<Guard page="channels"><ChannelsPage /></Guard>} />
          <Route path="products" element={<Guard page="products"><ProductsPage /></Guard>} />
          <Route path="prep" element={<Guard page="prep"><PrepPage /></Guard>} />
          <Route path="menu" element={<Guard page="menu"><MenuPage /></Guard>} />
          <Route path="alerts" element={<Guard page="alerts"><AlertsPage /></Guard>} />
          <Route path="accuracy" element={<Guard page="accuracy"><AccuracyPage /></Guard>} />
          <Route path="inputs" element={<Guard page="inputs"><InputsPage /></Guard>} />
          <Route path="quiet-days" element={<Guard page="quiet"><QuietDaysPage /></Guard>} />
          <Route path="influencers" element={<Guard page="influencers"><InfluencersPage /></Guard>} />
          <Route path="upload" element={<Guard page="upload"><UploadPage /></Guard>} />
          <Route path="settings" element={<Guard page="settings"><SiteSettingsPage /></Guard>} />
          <Route path="settings/group" element={<Guard page="group_settings"><GroupSettingsPage /></Guard>} />
          <Route path="settings/data-sharing" element={<Guard page="data_sharing"><DataSharingPage /></Guard>} />
          <Route path="group" element={<Guard page="group"><GroupPage /></Guard>} />
          <Route path="users" element={<Guard page="users"><UsersPage /></Guard>} />
          <Route path="*" element={<EmptyState title="Page not found">Use the menu to find your way back.</EmptyState>} />
        </Routes>
      </AppShell>
    </SessionProvider>
  );
}

export function App({ services }: { services: Services }) {
  const [queryClient] = useState(makeQueryClient);
  return (
    <ServicesProvider services={services}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/sign-up" element={<SignUpPage />} />
            <Route path="/*" element={<AuthedApp />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </ServicesProvider>
  );
}
